/* global axios */
import React, { useEffect, useState } from 'react';
import api from '../services/apiService';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

const UVVInspectionList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlCert = urlParams.get('certificateNumber');

  // ------------------------------------
  // Filter-State
  // ------------------------------------
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('uvvInspectionFilters');
    const init = saved
      ? JSON.parse(saved)
      : {
          certificateNumber: null,
          machine: null,
          productGroup: null,
          standardCode: null,
          uvvType: null,
          result: null,
          inspector: null,
          inspectionDate: null,
          // ⬇️ NEU
          customer: null,
          contact: null,
          operationLocation: null,
          inspectionToLW: null,
        };
    if (urlCert) {
      init.certificateNumber = { value: urlCert, label: urlCert };
    }
    return init;
  });

  useEffect(() => {
    localStorage.setItem('uvvInspectionFilters', JSON.stringify(filters));
    setCurrentPage(1);
  }, [filters]);

  // ------------------------------------
  // Server-seitige Filteroptionen
  // ------------------------------------
  const [serverFilterOptions, setServerFilterOptions] = useState({
    certificateNumbers: [],
    machines: [],
    productGroups: [],
    standardCodes: [],
    uvvTypes: ['main', 'retest'],
    results: ['pending', 'passed', 'failed'],
    inspectors: [],
    inspectionDates: [],
    // ⬇️ NEU
    customers: [],
    contacts: [],
    operationLocations: [],
    inspectionToLWValues: [true, false],
  });

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await api.get('/uvv/inspections/filter-options');
        setServerFilterOptions((prev) => ({
          ...prev,
          ...res.data,
        }));
      } catch (e) {
        console.error('Fehler beim Laden der UVV-Filteroptionen:', e);
      }
    };
    fetchFilterOptions();
  }, []);

  // ------------------------------------
  // Daten + Pagination
  // ------------------------------------
  const [rows, setRows] = useState([]);
  const [pages, setPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 25;

  const buildQuery = () => {
    const p = new URLSearchParams();
    p.append('limit', limit);
    p.append('page', currentPage);

    if (filters.certificateNumber) p.append('certificateNumber', filters.certificateNumber.value);
    if (filters.machine) p.append('machineId', filters.machine.value);
    if (filters.productGroup) p.append('productGroupId', filters.productGroup.value);
    if (filters.standardCode) p.append('standardCode', filters.standardCode.value);
    if (filters.uvvType) p.append('uvvType', filters.uvvType.value);
    if (filters.result) p.append('result', filters.result.value);
    if (filters.inspector) p.append('inspectorId', filters.inspector.value);
    if (filters.inspectionDate) p.append('inspectionDate', filters.inspectionDate.value);

    // ⬇️ NEU
    if (filters.customer) p.append('customerId', filters.customer.value);
    if (filters.contact) p.append('contactId', filters.contact.value);
    if (filters.operationLocation) p.append('operationLocation', filters.operationLocation.value);
    if (filters.inspectionToLW) p.append('inspectionToLW', filters.inspectionToLW.value);

    const qs = p.toString();
    return qs ? `?${qs}` : '';
  };

  useEffect(() => {
    const fetchInspections = async () => {
      try {
        const query = buildQuery();
        const res = await api.get(`/uvv/inspections/list${query}`);
        setRows(res.data.data || []);
        setPages(res.data.pages || 1);
      } catch (e) {
        console.error('Fehler beim Laden der UVV-Prüfungen:', e);
      }
    };
    fetchInspections();
  }, [filters, currentPage]);

  // ------------------------------------
  // Utils
  // ------------------------------------
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  const resultBadgeStyle = (result) => {
    const base = {
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: 12,
      color: '#fff',
      display: 'inline-block',
      textTransform: 'capitalize',
      textAlign: 'center',
      minWidth: 80,
    };
    if (result === 'passed') return { ...base, background: '#28a745' };
    if (result === 'failed') return { ...base, background: '#dc3545' };
    return { ...base, background: '#6c757d' };
  };

  const personName = (first, last) =>
    [first, last].filter(Boolean).join(' ').trim() || '–';

  // ------------------------------------
  // Aktionen
  // ------------------------------------
// außerhalb der Komponente (oder als Modul-Variable), damit die Referenz bleibt:
let uvvPdfWin = null;

const handleViewPDF = (inspectionId) => {
  const base = api?.defaults?.baseURL?.replace(/\/+$/, '') || '';
  // ⚠️ plural: /documents … (nicht /document)
  const url  = `${base}/documents/uvv/${inspectionId}/pdf`;

  // Ein einzelner, wiederverwendbarer Tab namens "uvv_pdf_tab"
  // (Wenn du pro Inspection einen eigenen Tab willst: `uvv_pdf_${inspectionId}`)
  uvvPdfWin = window.open(url, 'uvv_pdf_tab', 'noopener,noreferrer');

  // Optional: den Tab in den Vordergrund holen
  if (uvvPdfWin) uvvPdfWin.focus();
};

  const deleteInspection = async (id) => {
    if (!window.confirm('Möchten Sie diese UVV-Prüfung wirklich löschen?')) return;
    try {
      await api.delete(`/uvv/inspections/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      alert('Prüfung gelöscht.');
    } catch (e) {
      console.error('Fehler beim Löschen:', e);
      alert('Fehler beim Löschen der Prüfung.');
    }
  };

  const createInspectionDraftForMachine = async () => {
    navigate('/uvvInspectionForm');
  };

  // Mailing-Status pro Zeile
  const [mailingIds, setMailingIds] = useState(() => new Set());

  const setMailing = (id, isMailing) => {
    setMailingIds((prev) => {
      const next = new Set(prev);
      if (isMailing) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const isMailing = (id) => mailingIds.has(id);

  const sendUvvInspectionMail = async (inspectionId) => {
    if (isMailing(inspectionId)) return;
    try {
      setMailing(inspectionId, true);
      await api.post(`/documents/uvvinspection/${inspectionId}/mail`);
      alert('✅ Prüfbericht erfolgreich gemailt!');
    } catch (err) {
      console.error('Fehler beim Mailen des Prüfberichts:', err);
      alert(err?.response?.data?.message || 'Fehler beim Mailen des Prüfberichts!');
    } finally {
      setMailing(inspectionId, false);
    }
  };

  // ------------------------------------
  // Render
  // ------------------------------------
  return (
    <div className="uvv-inspection-list">
      <Helmet>
        <title>UVV-Berichte</title>
        <meta name="description" content="Liste der UVV-Prüfungen" />
      </Helmet>

      <h1>UVV-Berichte</h1>

      <button onClick={createInspectionDraftForMachine} style={{ marginBottom: 20 }}>
        Neue UVV-Prüfung anlegen
      </button>

      {/* Filter */}
      <div style={{ marginBottom: 20 }}>
        <h3>Filter</h3>
        {/* mehr Spalten, da 3 neue Filter */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 10 }}>
          <Select
            options={serverFilterOptions.certificateNumbers.map((v) => ({ value: v, label: v }))}
            placeholder="Zert.nr."
            isClearable
            value={filters.certificateNumber}
            onChange={(selected) => setFilters({ ...filters, certificateNumber: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={serverFilterOptions.machines.map((m) => ({ value: m.id || m.value, label: m.name || m.label }))}
            placeholder="Maschine"
            isClearable
            value={filters.machine}
            onChange={(selected) => setFilters({ ...filters, machine: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={serverFilterOptions.productGroups.map((pg) => ({ value: pg.id || pg.value, label: pg.name || pg.label }))}
            placeholder="Produktgruppe"
            isClearable
            value={filters.productGroup}
            onChange={(selected) => setFilters({ ...filters, productGroup: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={serverFilterOptions.standardCodes.map((v) => ({ value: v, label: v }))}
            placeholder="Norm"
            isClearable
            value={filters.standardCode}
            onChange={(selected) => setFilters({ ...filters, standardCode: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={(serverFilterOptions.uvvTypes || []).map((v) => ({ value: v, label: v === 'main' ? 'Hauptprüfung' : 'Nachprüfung' }))}
            placeholder="Prüfungsart"
            isClearable
            value={filters.uvvType}
            onChange={(selected) => setFilters({ ...filters, uvvType: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={(serverFilterOptions.results || []).map((v) => {
              const label = v === 'passed' ? 'Bestanden' : v === 'failed' ? 'Nicht bestanden' : 'Offen';
              return { value: v, label };
            })}
            placeholder="Ergebnis"
            isClearable
            value={filters.result}
            onChange={(selected) => setFilters({ ...filters, result: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={(serverFilterOptions.inspectors || []).map(u => ({
              value: u.id,
              label: u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || `#${u.id}`,
            }))}
            placeholder="Prüfer"
            isClearable
            value={filters.inspector}
            onChange={(selected) => setFilters({ ...filters, inspector: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
          <Select
            options={serverFilterOptions.inspectionDates.map((v) => ({ value: v, label: v }))}
            placeholder="Prüfdatum"
            isClearable
            value={filters.inspectionDate}
            onChange={(selected) => setFilters({ ...filters, inspectionDate: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />

          {/* ⬇️ NEU: Kunde */}
          <Select
            options={(serverFilterOptions.customers || []).map(c => ({
              value: c.id || c.value,
              label: c.label || c.company || [c.firstName, c.lastName].filter(Boolean).join(' ') || `#${c.id}`,
            }))}
            placeholder="Kunde"
            isClearable
            value={filters.customer}
            onChange={(selected) => setFilters({ ...filters, customer: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />

          {/* ⬇️ NEU: Besteller */}
          <Select
            options={(serverFilterOptions.contacts || []).map(k => ({
              value: k.id || k.value,
              label: k.label || [k.firstName, k.lastName].filter(Boolean).join(' ') || `#${k.id}`,
            }))}
            placeholder="Besteller"
            isClearable
            value={filters.contact}
            onChange={(selected) => setFilters({ ...filters, contact: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />

          {/* ⬇️ NEU: Einsatzort */}
          <Select
            options={(serverFilterOptions.operationLocations || []).map(v => ({ value: v, label: v }))}
            placeholder="Einsatzort"
            isClearable
            value={filters.operationLocation}
            onChange={(selected) => setFilters({ ...filters, operationLocation: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />

          {/* ⬇️ NEU: LW publish */}
          <Select
            options={(serverFilterOptions.inspectionToLWValues || [true, false]).map(v => ({
              value: String(v), // "true" | "false"
              label: v ? 'LW publish: Ja' : 'LW publish: Nein',
            }))}
            placeholder="LW publish"
            isClearable
            value={filters.inspectionToLW}
            onChange={(selected) => setFilters({ ...filters, inspectionToLW: selected })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </div>
      </div>

      {/* Tabelle */}
      {rows.length > 0 ? (
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Zertifikatsnr.</th>
              <th>Maschine</th>
              <th>Produktgruppe</th>
              {/* NEU */}
              <th>Kunde</th>
              <th>Besteller</th>
              <th>Einsatzort</th>
              {/* /NEU */}
              <th>Prüfer</th>
              <th>Prüfdatum</th>
              <th>Art</th>
              <th>Norm</th>
              <th>Ergebnis</th>
              <th>LW publish</th>
              <th>Aktionen</th>
              <th>Ausgabe</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const machineName = r.machine?.name || 'Unbekannt';
              const pgName = r.machine?.productGroup?.name || '–';

              const inspectorName = r.inspector
                ? personName(r.inspector.firstName, r.inspector.lastName)
                : (r.inspectorName || '–');

              const typeLabel = r.uvvType === 'retest' ? 'Nachprüfung' : 'Hauptprüfung';
              const resultLabel =
                r.result === 'passed' ? 'bestanden' : r.result === 'failed' ? 'nicht bestanden' : 'offen';

              const toLW = !!r.inspectionToLW;

              const customerLabel =
                r.customer?.company ||
                personName(r.customer?.firstName, r.customer?.lastName);

              const contactLabel = r.contact
                ? personName(r.contact.firstName, r.contact.lastName)
                : '–';

              const opLoc = r.operationLocation || '–';

              const mailing = isMailing(r.id);

              return (
                <tr key={r.id}>
                  <td>{r.id}</td>
                  <td>{r.certificateNumber}</td>
                  <td title={machineName}>
                    {machineName}
                    {r.machine?.serialNumber ? ` (${r.machine.serialNumber})` : ''}
                  </td>
                  <td>{pgName}</td>

                  {/* NEU */}
                  <td>{customerLabel}</td>
                  <td>{contactLabel}</td>
                  <td>{opLoc}</td>
                  {/* /NEU */}

                  <td>{inspectorName}</td>
                  <td>{formatDate(r.inspectionDate)}</td>
                  <td>{typeLabel}</td>
                  <td>{r.standardCode}</td>
                  <td>
                    <span style={resultBadgeStyle(r.result)}>{resultLabel}</span>
                  </td>
                  <td>
                    {toLW ? (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        background: '#28a745',
                        color: '#fff',
                        fontSize: 12,
                        display: 'inline-block'
                      }}>Ja</span>
                    ) : (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: 12,
                        background: '#6c757d',
                        color: '#fff',
                        fontSize: 12,
                        display: 'inline-block'
                      }}>Nein</span>
                    )}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/uvv/inspections/${r.id}/edit`)}
                      style={{
                        fontSize: 14,
                        padding: '5px 10px',
                        background: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 5,
                        marginRight: 8,
                        cursor: 'pointer',
                      }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteInspection(r.id)}
                      style={{
                        fontSize: 14,
                        padding: '5px 10px',
                        background: '#dc3545',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 5,
                        cursor: 'pointer',
                      }}
                    >
                      Löschen
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <button
                        onClick={() => handleViewPDF(r.id)}
                        style={{
                          fontSize: 14,
                          padding: '5px 10px',
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 5,
                          cursor: 'pointer',
                        }}
                        title="PDF-Ansicht"
                      >
                        PDF
                      </button>

                      <button
                        onClick={() => sendUvvInspectionMail(r.id)}
                        disabled={mailing}
                        style={{
                          fontSize: 14,
                          padding: '5px 10px',
                          background: mailing ? '#6c757d' : '#17a2b8',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 5,
                          cursor: mailing ? 'not-allowed' : 'pointer',
                        }}
                        title={mailing ? 'Versand läuft…' : 'Prüfbericht mailen'}
                      >
                        {mailing ? 'Mail wird gesendet…' : 'Mailen'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>Keine UVV-Prüfungen verfügbar.</p>
      )}

      {/* Pagination */}
      <div style={{ marginTop: '1rem' }}>
        <button
          onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
          disabled={currentPage === 1}
          style={{
            fontSize: 15,
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: 5,
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
          }}
        >
          Vorherige
        </button>
        <span style={{ margin: '0 1rem' }}>
          Seite {currentPage} von {pages}
        </span>
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage >= pages}
          style={{
            fontSize: 15,
            padding: '8px 12px',
            backgroundColor: 'rgb(155, 155, 155)',
            border: 'none',
            borderRadius: 5,
            cursor: currentPage >= pages ? 'not-allowed' : 'pointer',
          }}
        >
          Nächste
        </button>
      </div>
    </div>
  );
};

export default UVVInspectionList;
